'use client';

import { filterChildrenByType } from 'Shared/utils';
import { SIZE } from '@constants';
import type { ButtonProps, ColorStyledProps } from '@viewup/ui';
import {
  ArrowLeft,
  Box,
  Button,
  Column,
  getColorValue,
  Row,
  Typography,
  useTheme,
} from '@viewup/ui';
import { useIsBottomBarVisible } from 'app/_hook';
import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import type { ChildrenProps } from 'viewup-lib/common';

export interface SubHeaderLayoutProps extends ChildrenProps {
  bgColor?: ColorStyledProps['bgColor'];
  bottomButton?: Pick<ButtonProps, 'disabled' | 'onClick'> & {
    position?: number;
    text: string;
    variant?: ButtonProps['variant'];
  };
  headerLayout?: {
    rightContent?: ReactNode;
  };
  isBorderLine?: boolean;
  onClickBackArrow?: () => void;
  title: string;
}

export const SubHeaderLayout = (props: SubHeaderLayoutProps) => {
  const {
    title,
    headerLayout,
    onClickBackArrow,
    bottomButton,
    isBorderLine = true,
    bgColor = 'bg.primary',
  } = props;
  const router = useRouter();
  const theme = useTheme();
  const isBottomBarVisible = useIsBottomBarVisible();
  const bottomBoxRef = useRef<HTMLDivElement | null>(null);
  const [bottomContentHeight, setBottomContentHeight] = useState<number>(0);

  const remainContent = React.Children.toArray(props.children).filter(
    (child) => React.isValidElement(child) && child.type !== SubHeaderLayout.BottomContent,
  );
  const bottomContent = filterChildrenByType(props.children, SubHeaderLayout.BottomContent)?.[0];

  useEffect(() => {
    if (!bottomBoxRef.current) return;
    setBottomContentHeight(bottomBoxRef.current.clientHeight);
  }, [bottomBoxRef]);

  return (
    <Column bgColor={bgColor} css={{ position: 'relative' }} height={'100%'} width={'100%'}>
      <Row
        bgColor='bg.primary'
        css={{
          borderBottom: isBorderLine
            ? `1px solid ${getColorValue('border.tertiary', theme)}`
            : 'none',
          position: 'fixed',
        }}
        height={SIZE.HEADER_HEIGHT - 10}
        justifyContent='center'
        maxWidth={440}
        px={16}
        py={8}
        width={'100%'}
        zIndex={theme.zIndex.fixed[1000]}
      >
        <Column
          css={{
            alignItems: 'center',
            cursor: 'pointer',
            justifyContent: 'center',
            left: 16,
            position: 'absolute',
          }}
          onClick={() => {
            onClickBackArrow ? onClickBackArrow() : router.back();
          }}
        >
          <ArrowLeft />
        </Column>
        <Typography fontType='body2.medium'>{title}</Typography>

        {headerLayout?.rightContent && (
          <Box css={{ right: 16 }} height={'100%'} position='absolute'>
            {headerLayout.rightContent}
          </Box>
        )}
      </Row>

      <Box
        className='auto-click-wrapper'
        css={{ overflow: 'auto', paddingTop: SIZE.HEADER_HEIGHT - 10 }}
        height={'100%'}
      >
        {remainContent}
      </Box>
      {(bottomButton?.text || bottomContent) && !isBottomBarVisible && (
        <Box height={bottomContent ? bottomContentHeight + 6 : 82}></Box>
      )}
      {(bottomButton?.text || bottomContent) && isBottomBarVisible && (
        <Box height={bottomContent ? bottomContentHeight + 6 : SIZE.BOTTOM_BAR_HEIGHT + 16}></Box>
      )}

      {(bottomButton || bottomContent) && (
        <Box
          bgColor='bg.primary'
          className='auto-click-bottom'
          css={{
            borderTop: `1px solid ${getColorValue('border.tertiary', theme)}`,
            bottom:
              bottomButton?.position ??
              (isBottomBarVisible
                ? `calc(${SIZE.BOTTOM_BAR_HEIGHT - 1}px + env(safe-area-inset-bottom))`
                : `calc(${0}px + env(safe-area-inset-bottom))`),
            position: 'fixed',
            width: '100%',
          }}
          maxWidth={440}
          px={16}
          py={16}
          ref={bottomBoxRef}
          zIndex={theme.zIndex.fixed[1001]}
        >
          {bottomButton && (
            <Button
              disabled={bottomButton?.disabled}
              onClick={bottomButton.onClick}
              size='L'
              styledComponents={{
                root: { width: '100%' },
              }}
              variant={bottomButton.variant || 'solid.primary'}
            >
              {bottomButton.text}
            </Button>
          )}

          {bottomContent}
        </Box>
      )}
    </Column>
  );
};

SubHeaderLayout.BottomContent = (props: ChildrenProps) => {
  return props.children;
};
